import {Component, ViewChild, OnDestroy, ChangeDetectorRef, OnInit} from '@angular/core';
import { Subscription } from 'rxjs';

import { ModalComponent } from '../../modal/modal.component';
import { ModalEditComponent } from '../../modal/modal.edit.component';
import { ModalService } from '../../shared/services/modal.service';
import { HttpResponse } from '@angular/common/http';
import { SubmissionHistoryService } from '../../shared/services/submission-history.service';
import { SubmissionSearchUserInfoObject } from './search-object';

@Component({
    selector: 'dashboard',
    templateUrl: './dashboard.component.html',
    styleUrls: [ './dashboard.component.scss' ]
})
export class DashboardComponent implements OnDestroy, OnInit {

    @ViewChild('submissionSource')
    readonly modalSubmissionSource: ModalComponent;

    @ViewChild('editSubmissionSource')
    public modalEditSubmissionSource: ModalEditComponent;

    sourceModalUuid: string;
    // Tracks whether the currently-open source modal is for a DSA Antibody record.
    // DSA Antibody content is JSON rather than XML, so it needs to be downloaded
    // as a formatted JSON file.
    private sourceModalIsDsaAntibody = false;
    // The File ID (hmlId) of the currently-open source modal. Used to name
    // DSA Antibody downloads.
    private sourceModalFileId: string;
    public  submissionSourceModalTitle: string;
    public  submissionSourceModalXmlContent: string;
    public  editSubmissionSourceModalXmlContent: string;
    public  xmlWithProperty: string;
    private submissionSourceModalSubscription: Subscription;
    private userInfoSubscription: Subscription;
    public disabled: string;
    public injectProperty: boolean;
    private readonly propertyToInject = '<property name="internal-view" value="true"/>';
    userInfo: SubmissionSearchUserInfoObject = new SubmissionSearchUserInfoObject();


    // For a given file size (in bytes), returns a simplified size string to
    // append to the download button.
    // Example: sizeString(2345) -> "2 kB"
    private static sizeString(numBytes: number): string {
        let unit = numBytes;
        if (numBytes < 1000) {
            return Math.floor(unit) + ' B';
        }
        if (numBytes < 1000000) {
            unit /= 1000;
            return Math.floor(unit) + ' kB';
        }
        if (numBytes < 1000000000) {
            unit /= 1000000;
            return Math.floor(unit) + ' MB';
        }
        unit /= 1000000000;
        return Math.floor(unit) + ' GB';
    }

    constructor(readonly submissionHistoryService: SubmissionHistoryService,
                public ref: ChangeDetectorRef,
                readonly modalService: ModalService) {
    }

    public ngOnInit(): void {
        this.userInfoSubscription = this.submissionHistoryService.currentUserInfo.subscribe(message => this.userInfo = message);
        this.submissionSourceModalSubscription = this.modalService
            .submissionSourceModalRequests$
            .subscribe(modalRequest => {
                this.displaySubmissionSourceDialog(modalRequest.uuid, modalRequest.hmlId,
                    modalRequest.uploaderFileName, modalRequest.testType)
            });
    }

    public ngOnDestroy(): void {
        this.submissionSourceModalSubscription.unsubscribe();
    }

    public cleanEdit(): void {
        const menu = document.getElementById('edited-content');
        while (menu.childNodes.length > 1) {
            menu.removeChild(menu.lastChild);
        }
        menu.firstChild.textContent = document.getElementsByClassName('hml-source-text')[0].textContent
    }

    public displaySubmissionSourceDialog(uuid: string, hmlId: string, uploaderFileName: string, testType?: string): void {
        // Step 1: Temporarily clear the title and content areas of the dialog so that a previous
        // sample's contents don't get displayed.
        // TODO: In a future story, replace this with a loading spinner.
        this.submissionSourceModalTitle = 'Loading file...';
        this.submissionSourceModalXmlContent = '\n\n\n\n\n\n\n';
        this.modalSubmissionSource.reprocessButtonTitle('');

        // DSA Antibody records cannot be edited, resubmitted, or reprocessed from the dashboard,
        // so their source modal is view-only.
        const isDsaAntibody = testType === 'DSA Antibody';

        // Step 2: Load the actual content
        let title: string;
        if (isDsaAntibody) {
            title = 'Antibody Submission for ID: ' + hmlId;
        } else {
            title = 'HML Preview ID ' + hmlId;
            if ((uploaderFileName) != null && (uploaderFileName.trim() !== '')) {
                title += ' / File Name ' + uploaderFileName;
            }
        }

        this.modalSubmissionSource.triggerSpinner(true);
        this.modalSubmissionSource.small(true);
        this.submissionHistoryService
            .getSubmissionText(uuid)
            .then(response =>  {
                if (response) {
                    // Explicitly toggle reprocess/edit on or off so that state from a
                    // previously-opened modal does not carry over to this one.
                    const allowResubmitActions = this.userInfo.internalUser && !isDsaAntibody;
                    this.modalSubmissionSource.triggerReprocess(allowResubmitActions);
                    this.modalSubmissionSource.small(false);
                    this.submissionSourceModalTitle = title;
                    this.submissionSourceModalXmlContent = isDsaAntibody
                        ? JSON.stringify(JSON.parse(response.body), null, 2)
                        : response.body;
                    this.sourceModalUuid = uuid;
                    this.sourceModalIsDsaAntibody = isDsaAntibody;
                    this.sourceModalFileId = hmlId;
                    const contentLength = response.headers.get('X-FILE-SIZE');
                    const downloadSizeMsg = DashboardComponent.sizeString(Number(contentLength));
                    const cbTitle = `Download File (${downloadSizeMsg})`;
                    this.modalSubmissionSource.cancelButtonTitle(cbTitle);
                    this.modalSubmissionSource.okButtonTitle('Close');
                    this.modalSubmissionSource.resubmitButtonTitle('');
                    this.modalSubmissionSource.editButtonTitle(allowResubmitActions ? 'Edit' : '');
                    this.modalSubmissionSource.triggerSpinner(false);
                }
            });
        this.modalSubmissionSource.open();
    }

    public downloadFile(uuid: string): void {
        this.modalSubmissionSource.triggerReprocess(false);
        this.submissionHistoryService
            .getSubmissionText(uuid)
            .then((response: HttpResponse<string>) =>  {
                const isDsaAntibody = this.sourceModalIsDsaAntibody;

                // DSA Antibody content is JSON, so download it as a pretty-printed
                // .json file. All other content is XML and downloaded as-is.
                const body = isDsaAntibody
                    ? JSON.stringify(JSON.parse(response.body), null, 2)
                    : response.body;
                const mimeType = isDsaAntibody ? 'application/json' : 'application/xml';

                const blob = new Blob([body], { type: mimeType });
                const link = document.createElement('a');
                link.href = window.URL.createObjectURL(blob);

                let filename: string;
                if (isDsaAntibody) {
                    // Name DSA Antibody downloads by their File ID with a .json extension,
                    // rather than the server-provided file name.
                    filename = this.sourceModalFileId + '.json';
                } else {
                    filename = response.headers.get('X-NMDP-FILE-NAME');
                }
                link.download = filename;
                link.click();
                window.URL.revokeObjectURL(link.href);
            });
    }

    public editFile(): void {
        this.modalSubmissionSource.triggerReprocess(false);
        this.cleanEdit();
        this.modalSubmissionSource.close();
        this.editSubmissionSourceModalXmlContent = this.submissionSourceModalXmlContent;
        this.injectProperty = this.editSubmissionSourceModalXmlContent.includes(this.propertyToInject);
        this.modalEditSubmissionSource.resubmitButtonTitle('Submit');
        this.modalEditSubmissionSource.open();
    }

    public confirmResubmit(): void {
        this.modalEditSubmissionSource.close();
        this.modalSubmissionSource.editButtonTitle('');
        this.modalSubmissionSource.small(true);
        this.editSubmissionSourceModalXmlContent = document.getElementById('edited-content').innerText
        this.submissionSourceModalTitle = 'Confirm Resubmission';
        this.submissionSourceModalXmlContent = '';
        this.modalSubmissionSource.resubmitButtonTitle('Submit');
        this.modalSubmissionSource.okButtonTitle('Cancel');
        this.modalSubmissionSource.cancelButtonTitle('');
        this.modalSubmissionSource.open();
    }

    public resubmitFile(resubmissionXml: string): void {
        this.submissionSourceModalXmlContent = 'Submitting....';
        this.modalSubmissionSource.resubmitButtonTitle('');
        this.modalSubmissionSource.okButtonTitle('Close');
        this.submissionHistoryService
            .resubmitSubmission(resubmissionXml)
            .then((response: any) =>  {
                this.modalSubmissionSource.close();
                this.modalSubmissionSource.small(true);
                this.submissionSourceModalTitle = 'Submission Result';
                this.submissionSourceModalXmlContent  = response.message;
                this.modalSubmissionSource.resubmitButtonTitle('');
                this.modalSubmissionSource.open();
            });
    }

    public confirmReprocess(): void {
        this.modalEditSubmissionSource.close();
        this.modalSubmissionSource.editButtonTitle('');
        this.modalSubmissionSource.small(true);
        this.modalSubmissionSource.triggerReprocess(false);
        this.submissionSourceModalTitle = 'Confirm Reprocessing';
        this.submissionSourceModalXmlContent = '';
        this.modalSubmissionSource.resubmitButtonTitle('');
        this.modalSubmissionSource.reprocessButtonTitle('Reprocess');
        this.modalSubmissionSource.okButtonTitle('Cancel');
        this.modalSubmissionSource.cancelButtonTitle('');
        this.modalSubmissionSource.open();
    }

    public reprocessFile(uuid: string): void {
        this.submissionSourceModalTitle = 'Reprocessing Submission';
        this.submissionSourceModalXmlContent = '';
        this.modalSubmissionSource.reprocessButtonTitle('');
        this.modalSubmissionSource.small(true);
        this.modalSubmissionSource.triggerReprocess(false);
        this.modalSubmissionSource.editButtonTitle('');
        this.modalSubmissionSource.cancelButtonTitle('');
        this.modalSubmissionSource.triggerSpinner(true);
        this.modalSubmissionSource.resubmitButtonTitle('');
        this.modalSubmissionSource.okButtonTitle('Close');
        this.submissionHistoryService
            .reprocessSubmission(uuid)
            .then((response: any) =>  {
                this.modalSubmissionSource.close();
                this.modalSubmissionSource.triggerSpinner(false);
                this.modalSubmissionSource.small(true);
                this.submissionSourceModalTitle = 'Submission Result';
                this.submissionSourceModalXmlContent  = response.message;
                this.modalSubmissionSource.resubmitButtonTitle('');
                this.modalSubmissionSource.open();
            });
    }

    addEditProp(injectProperty: boolean) {
        this.injectProperty = injectProperty;
        const editedContent = document.getElementById('edited-content');
        this.xmlWithProperty = editedContent.textContent.replace(/\u00A0/g, ' ').replace(/\uFFFD/g, '');

        if (injectProperty && !this.xmlWithProperty.includes(this.propertyToInject)) {
            const match = this.xmlWithProperty.match(/<hml(\s[^>]*)?>/);
            if (match) {
                const insertAt = match.index + match[0].length;
                this.xmlWithProperty = this.xmlWithProperty.substring(0, insertAt) +
                    this.propertyToInject +
                    this.xmlWithProperty.substring(insertAt);
            }
        } else if (!injectProperty) {
            this.xmlWithProperty = this.xmlWithProperty.replace(/<property name="internal-view"[^/]*\/>/g, '');
        }

        const sourceText = editedContent.querySelector('.hml-source-text') || editedContent;
        sourceText.textContent = this.xmlWithProperty;
    }
}
